import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  UserPlus, Users as UsersIcon, Mail, Lock, Shield,
  ChevronRight, Copy, CheckCheck, AlertTriangle,
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Can } from '@/hooks/usePermission'
import { ApiError } from '@/services/api/client'
import { getWorkspaceUsers, inviteUser } from '@/services/api/workspace.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'


export function UsersPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { lockedResourceIds } = useSubscriptionStore()

  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['workspace-users'],
    queryFn: getWorkspaceUsers,
  })

  const inviteMutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-users'] })
      setInviteLink(data.inviteLink)
      toast.success(`Invite sent to ${inviteEmail}`)
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.detail : 'Failed to send invite'
      toast.error(msg)
    },
  })

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    inviteMutation.mutate({ email: inviteEmail.trim() })
  }

  const handleCopyLink = () => {
    if (!inviteLink) return
    navigator.clipboard.writeText(inviteLink)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2000)
  }

  const handleInviteClose = () => {
    setInviteOpen(false)
    setInviteEmail('')
    setInviteLink(null)
    setLinkCopied(false)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Users</h1>
          <p className="mt-1 text-neutral-500">Manage workspace members and their permissions</p>
        </div>
        <Can permission={PERMISSION_KEYS.WORKSPACE_INVITE}>
          <Button onClick={() => setInviteOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite User
          </Button>
        </Can>
      </div>

      {/* User list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : users.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <UsersIcon className="h-12 w-12 text-neutral-300" />
            <h3 className="mt-4 text-lg font-medium">No users yet</h3>
            <p className="mt-1 text-sm text-neutral-500">Invite someone to get started</p>
            <Button className="mt-4" onClick={() => setInviteOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Invite User
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {users.map((user) => {
            const isLocked = lockedResourceIds.has(user.userId)
            return (
              <Card
                key={user.userId}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => navigate(`/users/${user.userId}`)}
              >
                <CardContent className="flex items-center gap-4 p-4">
                  {/* Avatar */}
                  <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700 font-semibold text-sm select-none">
                    {user.name.charAt(0).toUpperCase()}
                    {isLocked && (
                      <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 ring-2 ring-white">
                        <Lock className="h-2.5 w-2.5 text-amber-600" />
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-neutral-900 truncate">{user.name}</span>
                      {(user.owner || user.admin) && (
                        <Badge variant="default" className="gap-1 bg-primary-600 text-xs">
                          <Shield className="h-3 w-3" />
                          {user.owner ? 'Owner' : 'Admin'}
                        </Badge>
                      )}
                      {isLocked && (
                        <Badge variant="outline" className="gap-1 border-amber-300 text-amber-700 text-xs">
                          <Lock className="h-3 w-3" />
                          Locked
                        </Badge>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 text-sm text-neutral-500">
                      <Mail className="h-3.5 w-3.5" />
                      <span className="truncate">{user.email}</span>
                    </div>
                    {user.roles.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {user.roles.map((role) => (
                          <Badge key={role} variant="secondary" className="text-xs">{role}</Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  <ChevronRight className="h-5 w-5 shrink-0 text-neutral-400" />
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={(open) => { if (!open) handleInviteClose() }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite a User</DialogTitle>
            {inviteLink ? (
              <DialogDescription asChild>
                <div className="space-y-0.5">
                  <p>Invite sent to <strong className="text-neutral-900">{inviteEmail}</strong>.</p>
                  <p>You can also share the link directly:</p>
                  <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">Check spam folder</p>
                      <p className="mt-0.5">If the invitee doesn't see the email, ask them to check their <strong>spam or junk folder</strong>.</p>
                    </div>
                  </div>
                </div>
              </DialogDescription>
            ) : (
              <DialogDescription>
                An invitation email will be sent to the address below. The link expires in 7 days.
              </DialogDescription>
            )}
          </DialogHeader>

          {inviteLink ? (
            <div className="w-full overflow-hidden space-y-4">
              <div className="flex min-w-0 items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-neutral-700">{inviteLink}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={handleCopyLink}>
                  {linkCopied ? (
                    <CheckCheck className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <DialogFooter>
                <Button onClick={handleInviteClose}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleInviteSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email address</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleInviteClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={inviteMutation.isPending || !inviteEmail.trim()}>
                  <Mail className="mr-2 h-4 w-4" />
                  {inviteMutation.isPending ? 'Sending…' : 'Send Invite'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
